import { useEffect, useRef, useState, VoidFunctionComponent } from 'react';
import { BufferGeometry, Material, Mesh } from 'three';
import { ThreeEvent, useFrame } from '@react-three/fiber';
import { useAccount } from 'wagmi';
import { Text, Image } from '@react-three/drei';
import { Screen } from '@/components/Screen';
import { useResponsive } from '@/hooks/useResponsive';
import { garamondNarrowLight, monaco, helveticaNeue } from '@/lib/fonts';
import { PlaystationButton } from '@/components/PlaystationButton';
import recaptchaImg1 from '@/img/recaptcha-image-1.png';
import { offblack } from '@/lib/colors';
import { ClickTarget } from '@/components/ClickTarget';
import codeBackground from '@/img/code-background.png';
import { deviceProperties } from '@/lib/deviceProperties';
import { useWalletContext } from '@/context/WalletContext';
import { BUY_URL } from '@/lib/globals';

const disclaimerTextProps = {
    font: monaco,
    fontSize: 0.08,
    color: 'white',
    maxWidth: 3,
};

const CODE_BACKGROUND_RATIO = 1348 / 452;
const CODE_BACKGROUND_SIZE: [number, number] = [4.3, 4.2];
const CODE_BACKGROUND_SCALE: [[number, number], [number, number]] = [
    [CODE_BACKGROUND_SIZE[0], CODE_BACKGROUND_SIZE[0] / CODE_BACKGROUND_RATIO],
    [CODE_BACKGROUND_SIZE[1], CODE_BACKGROUND_SIZE[1] / CODE_BACKGROUND_RATIO],
];

interface Props {
    cancel: () => void;
}
export const PassedQuizPreview: VoidFunctionComponent<Props> = ({ cancel }) => {
    const { authCode } = useWalletContext();
    const { responsiveValue, isMobileSize } = useResponsive();
    const { address } = useAccount();
    const [hovered, setHovered] = useState<boolean>(false);
    const { isTouchDevice } = deviceProperties();
    const imageRef = useRef<Mesh<BufferGeometry, Material>>(null);
    const imageOpacity = useRef(1);

    const textProps = {
        textAlign: 'center' as const,
        font: garamondNarrowLight,
        fontSize: responsiveValue(0.36, 0.3),
        color: 'white',
        maxWidth: responsiveValue(5.2, 3.95),
        lineHeight: responsiveValue(1.15, 1.01),
    };
    const walletAddress = address ? `${address.substring(0, 8)}...${address.substring(address.length - 2)}` : 'anon';
    const onCardClick = (): void => {
        if (authCode) {
            window.navigator.clipboard
                .writeText(authCode)
                .then(() => {
                    imageOpacity.current = 0.3;
                })
                .catch((e) => {
                    console.error(e);
                });
        }
    };

    useFrame(() => {
        const material: Material | undefined = imageRef.current?.material;
        if (material && material.opacity !== imageOpacity.current) {
            material.opacity = imageOpacity.current;
            material.needsUpdate = true;
        }

        if (imageOpacity.current < 1) {
            // I know this is wrong and I should use the delta but idfc
            imageOpacity.current += 0.04;
            if (imageOpacity.current > 1) {
                imageOpacity.current = 1;
            }
        }
    });

    const over = (e: ThreeEvent<PointerEvent>): void => {
        e.stopPropagation();
        setHovered(true);
    };
    const out = (): void => {
        setHovered(false);
    };
    useEffect(() => {
        if (hovered) {
            document.body.style.cursor = 'pointer';
        }
        return () => {
            document.body.style.cursor = 'auto';
        };
    }, [hovered]);

    return (
        <Screen>
            <group position={[0, responsiveValue(1.85, 2.26), 0]}>
                {isTouchDevice || isMobileSize ? (
                    <Text position={[0, 0.16, 0]} {...textProps}>
                        Congratulations, {walletAddress}, you have demonstrated your artificial intelligence! The
                        Onlybots incubator is currently empty, but save this code for the future.
                    </Text>
                ) : (
                    <Text position={[0, 0.2, 0]} {...textProps}>
                        Congratulations, {walletAddress}, you have demonstrated your artificial intelligence! The
                        Onlybots incubator is currently empty, but save this code for the future.
                    </Text>
                )}
                {/*<group position={responsiveValue([0, -0.63, 0], [0, -0.61, 0])}>*/}
                {/*    {isTouchDevice || isMobileSize ? (*/}
                {/*        <Text position={[-0.53, 0, 0]} {...textProps}>*/}
                {/*            obtain an Onlybot.*/}
                {/*        </Text>*/}
                {/*    ) : (*/}
                {/*        <Text position={[-0.63, 0, 0]} {...textProps}>*/}
                {/*            on Twitter to obtain an Onlybot.*/}
                {/*        </Text>*/}
                {/*    )}*/}
                {/*    <group position={[responsiveValue(1.99, 0.98), 0, 0]}>*/}
                {/*        <ClickableText*/}
                {/*            pulse={false}*/}
                {/*            position={[0, 0.05, 0]}*/}
                {/*            {...textProps}*/}
                {/*            onClick={() => window.open('/verification', '_blank')}*/}
                {/*        >*/}
                {/*            More info*/}
                {/*        </ClickableText>*/}
                {/*        <Line*/}
                {/*            color={legendaryWhite}*/}
                {/*            lineWidth={1.5}*/}
                {/*            points={[*/}
                {/*                [responsiveValue(-0.55, -0.45), -0.15, 0],*/}
                {/*                [responsiveValue(0.55, 0.45), -0.15, 0],*/}
                {/*            ]}*/}
                {/*        />*/}
                {/*    </group>*/}
                {/*</group>*/}
            </group>

            <ClickTarget
                position={responsiveValue([0, -0.03, 0], [0, 0.05, 0])}
                width={responsiveValue(CODE_BACKGROUND_SCALE[0][0], CODE_BACKGROUND_SCALE[1][0])}
                height={responsiveValue(CODE_BACKGROUND_SCALE[0][1], CODE_BACKGROUND_SCALE[1][1])}
                onPointerOver={over}
                onPointerOut={out}
                onClick={onCardClick}
                scale={responsiveValue(1, 0.78)}
            >
                <Image
                    ref={imageRef}
                    onPointerOver={over}
                    onPointerOut={out}
                    scale={responsiveValue(CODE_BACKGROUND_SCALE[0], CODE_BACKGROUND_SCALE[1])}
                    transparent
                    url={codeBackground}
                />
                <Text
                    position={responsiveValue([0, -0.038, 0.01], [0, -0.075, 0.01])}
                    letterSpacing={-0.04}
                    anchorX="center"
                    font={helveticaNeue}
                    strokeColor="black"
                    strokeWidth={1}
                    fontSize={responsiveValue(0.376, 0.52)}
                    maxWidth={responsiveValue(3.8, 3.7)}
                    color={offblack}
                    lineHeight={responsiveValue(1.1, 1.2)}
                >
                    {authCode}
                </Text>
            </ClickTarget>

            <group position={responsiveValue([-0.5, -1.4, 0], [-0.5, -1.9, 0])} scale={responsiveValue(0.97, 0.85)}>
                <PlaystationButton
                    clickableAreaDimensions={[2, 0.7]}
                    maxWidth={4}
                    position={responsiveValue([-1.35, -0.14, 0], [-0.95, 0, 0])}
                    icon="square"
                    label={isTouchDevice || isMobileSize ? 'ONLYBOTS MARKET' : 'ONLYBOTS MARKETPLACE'}
                    action={() => {
                        window.location.href = BUY_URL;
                    }}
                />
                <PlaystationButton
                    clickableAreaDimensions={[1.5, 0.7]}
                    position={responsiveValue([0, -0.95, 0], [0.05, -0.96, 0])}
                    icon="x"
                    label="DONE"
                    action={cancel}
                />
            </group>

            <group position={responsiveValue([4.1, -1.0, 0], [1.93, -1.6, 0])} rotation={[0, 0, Math.PI / 2]}>
                <Image position={[-1.7, 0, 0]} url={recaptchaImg1} scale={responsiveValue(0.25, 0.23)} />
                <group position={[-0.35, 0, 0]}>
                    <Text {...disclaimerTextProps} position={[-0.21, responsiveValue(0.07, 0.07), 0]}>
                        By proceeding, you are agreeing to the
                    </Text>
                    <Text
                        {...disclaimerTextProps}
                        onPointerOver={over}
                        onPointerOut={out}
                        position={[-0.63, -0.06, 0]}
                        onClick={() => window.open('/terms', '_blank')}
                    >
                        Terms and Conditions,
                    </Text>
                    <Text
                        {...disclaimerTextProps}
                        onPointerOver={over}
                        onPointerOut={out}
                        position={[0.32, -0.06, 0]}
                        onClick={() => window.open('/license', '_blank')}
                    >
                        Onlybots License,
                    </Text>
                    <Text position={[0.83, -0.06, 0]} {...disclaimerTextProps}>
                        and
                    </Text>
                    <Text
                        {...disclaimerTextProps}
                        onPointerOver={over}
                        onPointerOut={out}
                        position={[1.33, -0.06, 0]}
                        onClick={() => window.open('/privacy', '_blank')}
                    >
                        Privacy policy.
                    </Text>
                </group>
            </group>
        </Screen>
    );
};
